<template>
    <portal to="modal" v-if="isOpen">
        <Modal
            :show="isOpen"
            size="large"
            @onModalClose="handleClose"
            class="EntityDetailsModal-Modal"
        >
            <template #header>
                <EntityDetailsHeader
                    :entity-type="entityType"
                    :entity-name="entityName"
                    :entity-subtitle="entitySubtitle"
                />
            </template>

            <div class="EntityDetailsModal">
                <div v-if="isLoading" class="EntityDetailsModal-Loading">
                    <div class="loading-spinner"></div>
                    <p>Daten werden geladen...</p>
                </div>

                <template v-else>
                    <EntityDetailsStats :is-loading="isLoading" :stats="calculatedStats" />

                    <div
                        v-if="entityType === 'driver' && driverCashJournal"
                        class="EntityDetailsModal-CashJournal"
                    >
                        <h3 class="cash-journal-title">Kassenbuch</h3>
                        <div class="cash-journal-grid">
                            <OverviewMetricCard
                                title="Kassenstand"
                                :value="cashJournalBalance"
                                icon="ri-wallet-3-line"
                                theme="green"
                                size="small"
                                :loading="isLoading"
                            />
                            <OverviewMetricCard
                                title="Transaktionen"
                                :value="cashJournalTransactionsCount"
                                icon="ri-exchange-line"
                                theme="blue"
                                size="small"
                                :loading="isLoading"
                            />
                            <OverviewMetricCard
                                title="Tagesberichte"
                                :value="cashJournalReportsCount"
                                icon="ri-file-list-3-line"
                                theme="orange"
                                size="small"
                                :loading="isLoading"
                            />
                        </div>
                    </div>

                    <EntityDetailsChart
                        :shifts="shifts"
                        :current-month="currentMonth"
                        :is-loading="isLoading"
                    />

                    <Tabs v-model="activeTableTab" :tabs="tableTabs">
                        <template #default="{ active }">
                            <div v-if="active === 'shifts'">
                                <EntityDetailsShifts :shifts="shifts" :is-loading="isLoading" />
                            </div>

                            <div v-if="active === 'transactions' && entityType === 'driver'">
                                <EntityDetailsCashJournalTransactions
                                    :transactions="cashJournalTransactions"
                                    :is-loading="isLoading"
                                    :current-month="currentMonth"
                                />
                            </div>
                        </template>
                    </Tabs>

                    <EntityDetailsQuickActions
                        :show="showQuickActions"
                        :entity-type="entityType"
                        :is-plan-basic="isPlanBasic"
                        @navigate="handleNavigate"
                    />
                </template>
            </div>
        </Modal>
    </portal>
</template>

<script>
import axios from 'axios';
import { formatISO, startOfMonth, endOfMonth, format, parseISO, isWithinInterval } from 'date-fns';
import { Portal } from 'portal-vue';
import { mapGetters, mapActions } from 'vuex';
import Modal from '@/components/widgets/Modal.vue';
import Tabs from '@/components/widgets/Tabs.vue';
import EntityDetailsHeader from './components/EntityDetailsHeader.vue';
import EntityDetailsStats from './components/EntityDetailsStats.vue';
import EntityDetailsChart from './components/EntityDetailsChart.vue';
import EntityTableShifts from './components/EntityTableShifts.vue';
import EntityDetailsQuickActions from './components/EntityDetailsQuickActions.vue';
import EntityTableCashJournalTransactions from './components/EntityTableCashJournalTransactions.vue';
import OverviewMetricCard from '@/components/OverviewMetricCard.vue';
import { calculateEntityStats } from './helpers/statsCalculator';
import { createKeyboardHandler } from './helpers/keyboardHandler';

export default {
    name: 'EntityDetailsModal',
    components: {
        Portal,
        Modal,
        Tabs,
        EntityDetailsHeader,
        EntityDetailsStats,
        EntityDetailsChart,
        EntityTableShifts,
        EntityTableCashJournalTransactions,
        EntityDetailsQuickActions,
        OverviewMetricCard,
    },
    props: {
        isOpen: {
            type: Boolean,
            default: false,
        },
        entityType: {
            type: String,
            default: 'driver',
        },
        entityId: {
            type: [String, Number],
            default: null,
        },
        entityName: {
            type: String,
            default: '',
        },
        entitySubtitle: {
            type: String,
            default: '',
        },
        entityData: {
            type: Object,
            default: null,
        },
        revenue: {
            type: Number,
            default: 0,
        },
        daysWorked: {
            type: Number,
            default: 0,
        },
        euroPerKm: {
            type: Number,
            default: 0,
        },
        hoursWorked: {
            type: Number,
            default: 0,
        },
    },
    data() {
        return {
            isLoading: false,
            shifts: [],
            allShifts: [],
            workTimeData: [],
            currentMonth: new Date(),
            activeTableTab: 'shifts',
            calculatedStats: {
                revenue: 0,
                daysWorked: 0,
                euroPerKm: 0,
                hoursWorked: 0,
                shiftTime: 0,
                workTime: 0,
                pauseTime: 0,
            },
        };
    },
    watch: {
        isOpen(newVal) {
            if (newVal && this.entityId) {
                this.currentMonth = new Date();
                this.fetchShiftsData();
            }
            if (newVal) {
                document.addEventListener('keydown', this.handleKeydown);
            } else {
                document.removeEventListener('keydown', this.handleKeydown);
            }
        },
    },
    computed: {
        ...mapGetters(['isPlanBasic']),
        ...mapGetters('cashJournals', ['currentJournal', 'activeJournalByDriver']),

        driverCashJournal() {
            if (this.entityType !== 'driver' || !this.entityId) {
                return null;
            }
            if (this.currentJournal?.driver?.driverNumber === Number(this.entityId)) {
                return this.currentJournal;
            }
            return this.activeJournalByDriver(Number(this.entityId));
        },

        cashJournalBalance() {
            if (!this.driverCashJournal) return '€0,00';

            const transactions = this.driverCashJournal.journalTransactions || [];
            const balance = transactions.reduce((sum, t) => sum + (t.amount || 0), 0);

            const balanceInEuros = balance / 100;
            return new Intl.NumberFormat('de-DE', {
                style: 'currency',
                currency: 'EUR',
            }).format(balanceInEuros);
        },

        cashJournalTransactionsCount() {
            if (!this.driverCashJournal) return 0;
            return this.driverCashJournal.journalTransactions?.length || 0;
        },

        cashJournalReportsCount() {
            if (!this.driverCashJournal) return 0;
            return this.driverCashJournal.dailyJournalReports?.length || 0;
        },

        cashJournalTransactions() {
            if (!this.driverCashJournal) return [];
            return this.driverCashJournal.journalTransactions || [];
        },

        tableTabs() {
            const tabs = [
                {
                    key: 'shifts',
                    label: 'Schichten',
                    icon: 'calendar-check',
                    badge: this.shifts.length,
                },
            ];

            if (this.entityType === 'driver') {
                tabs.push({
                    key: 'transactions',
                    label: 'Kassenbuch',
                    icon: 'wallet-3',
                    badge: this.filteredCashJournalTransactionsCount,
                });
            }

            return tabs;
        },

        filteredCashJournalTransactionsCount() {
            if (!this.driverCashJournal || !this.currentMonth) return 0;

            const transactions = this.cashJournalTransactions;
            if (!transactions.length) return 0;

            const monthStart = startOfMonth(this.currentMonth);
            const monthEnd = endOfMonth(this.currentMonth);

            return transactions.filter((transaction) => {
                if (!transaction.timestamp) return false;
                const transactionDate = parseISO(transaction.timestamp);
                return isWithinInterval(transactionDate, {
                    start: monthStart,
                    end: monthEnd,
                });
            }).length;
        },

        showQuickActions() {
            return this.isOpen && !this.isLoading && this.entityId;
        },

        canNavigateNext() {
            const nextMonth = new Date(this.currentMonth);
            nextMonth.setMonth(nextMonth.getMonth() + 1);
            return nextMonth <= new Date();
        },
        canNavigatePrev() {
            return true;
        },
        showButtons() {
            return {
                showRevenueButton: this.isPlanBasic && this.entityType === 'driver',
                showWorkingTimeButton: this.isPlanBasic && this.entityType === 'driver',
                showShiftsButton: this.isPlanBasic,
                showTripsButton: this.isPlanBasic,
                showCardPaymentsButton: this.isPlanBasic && this.entityType === 'driver',
                showEmployeesButton: this.isPlanBasic && this.entityType === 'driver',
                showOperationRideButton: this.isPlanBasic && this.entityType === 'car',
                showCarsButton: this.isPlanBasic && this.entityType === 'car',
            };
        },
    },
    methods: {
        ...mapActions('cashJournals', ['fetchDriverJournals', 'fetchJournal']),

        async fetchShiftsData() {
            if (!this.entityId) return;

            this.isLoading = true;

            try {
                const startOfSelectedMonth = startOfMonth(this.currentMonth);
                const endOfSelectedMonth = endOfMonth(this.currentMonth);

                const queryData = {
                    rangeStartAt: formatISO(startOfSelectedMonth),
                    rangeEndAt: formatISO(endOfSelectedMonth),
                };

                const entityQueryMap = {
                    driver: () => ({ ...queryData, driverNumber: this.entityId }),
                    car: () => ({ ...queryData, licenseNumber: this.entityId }),
                    concession: () => ({ ...queryData, concession: this.entityId }),
                };

                const url = `${import.meta.env.VITE_API_BASE_URL}/cpanel/shifts`;
                const result = await axios.post(
                    url,
                    entityQueryMap[this.entityType]?.() || queryData,
                    { withCredentials: true },
                );

                this.shifts = result.data.filter((s) => !s.isSystemDriver);

                if (this.entityType === 'driver') {
                    const allShiftsResult = await axios.post(url, queryData, {
                        withCredentials: true,
                    });
                    this.allShifts = allShiftsResult.data.filter((s) => !s.isSystemDriver);

                    const workHoursResult = await axios.post(
                        `${import.meta.env.VITE_API_BASE_URL}/cpanel/work_hours/daily/driver/${this.entityId}`,
                        {
                            rangeStartAt: format(startOfSelectedMonth, 'yyyy-MM-dd'),
                            rangeEndAt: format(endOfSelectedMonth, 'yyyy-MM-dd'),
                        },
                        { withCredentials: true },
                    );
                    this.workTimeData = workHoursResult.data;

                    await this.fetchDriverCashJournal();
                } else {
                    this.allShifts = this.shifts;
                    this.workTimeData = [];
                }

                this.calculateStats();
            } catch (error) {
                this.$toasted?.show('Fehler beim Laden der Daten.', { type: 'error' });
            } finally {
                this.isLoading = false;
            }
        },

        async fetchDriverCashJournal() {
            if (!this.entityId || this.entityType !== 'driver') return;

            try {
                await this.fetchDriverJournals({
                    driverNumber: Number(this.entityId),
                });

                const activeJournal = this.activeJournalByDriver(Number(this.entityId));

                if (activeJournal) {
                    await this.fetchJournal(activeJournal.uuid);
                }
            } catch (error) {
                console.warn('Failed to fetch cash journal:', error);
            }
        },

        calculateStats() {
            this.calculatedStats = calculateEntityStats(
                this.shifts,
                this.workTimeData,
                this.allShifts,
                this.entityType,
                this.entityId,
            );
        },

        handleClose() {
            this.$emit('close');
        },

        handleNavigate(page) {
            const currentQuery = this.$route.query;
            const entityQueryMap = {
                driver: 'driver',
                car: 'car',
                concession: 'concession',
            };

            const query = {
                [entityQueryMap[this.entityType]]: this.entityId,
                ...(currentQuery.from && { from: currentQuery.from }),
                ...(currentQuery.to && { to: currentQuery.to }),
                ...(currentQuery.driver &&
                    this.entityType !== 'driver' && { driver: currentQuery.driver }),
                ...(currentQuery.car && this.entityType !== 'car' && { car: currentQuery.car }),
                ...(currentQuery.concession &&
                    this.entityType !== 'concession' && { concession: currentQuery.concession }),
            };

            this.$router.push({ path: `/${page}`, query });
            this.handleClose();
        },

        navigateMonth(direction) {
            const newMonth = new Date(this.currentMonth);
            newMonth.setMonth(newMonth.getMonth() + direction);

            if (newMonth > new Date()) {
                return;
            }

            this.currentMonth = newMonth;
            this.fetchShiftsData();
        },
        handleKeydown(e) {
            if (!this.isOpen) return;

            const handler = createKeyboardHandler(
                this.entityType,
                this.isPlanBasic,
                this.showButtons,
                this.handleNavigate,
                this.navigateMonth,
                this.canNavigatePrev,
                this.canNavigateNext,
            );
            handler(e);
        },
    },
    beforeDestroy() {
        document.removeEventListener('keydown', this.handleKeydown);
    },
};
</script>

<style lang="scss">
.EntityDetailsModal-Modal {
    .Modal-Content {
        max-width: 800px !important;
    }

    .EntityDetailsModal {
        min-height: 200px;
    }

    .EntityDetailsModal-CashJournal {
        margin-bottom: 24px;

        .cash-journal-title {
            font-size: 14px;
            font-weight: 600;
            color: var(--color-text-black);
            margin: 0 0 12px 0;
        }

        .cash-journal-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 8px;
        }
    }

    @media (max-width: 768px) {
        .EntityDetailsModal-CashJournal {
            margin-bottom: 20px;

            .cash-journal-grid {
                grid-template-columns: 1fr;
                gap: 8px;
            }
        }
    }

    .EntityDetailsModal-Loading {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 60px 24px;
        text-align: center;

        .loading-spinner {
            width: 40px;
            height: 40px;
            border: 4px solid #f3f3f3;
            border-top: 4px solid var(--color-blue, #3498db);
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-bottom: 16px;
        }

        p {
            color: var(--color-gray, #666);
            font-size: 14px;
            margin: 0;
        }
    }

    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }

    .EntityDetailsModal-MonthNavigation {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 16px;
        padding: 12px 16px;
        background: rgba(0, 0, 0, 0.02);
        border-radius: 8px;
        gap: 12px;

        .month-nav-button {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 8px 12px;
            border: 0.5px solid var(--color-gray-light, #e5e7eb);
            background: white;
            border-radius: 6px;
            font-size: 14px;
            color: var(--color-text-black, #1a1a1a);
            cursor: pointer;
            transition: all 0.2s ease;

            &:hover:not(.is-disabled) {
                background: var(--color-gray-light, #f9fafb);
                border-color: var(--color-blue, #3498db);
            }

            &.is-disabled {
                opacity: 0.5;
                cursor: not-allowed;
                background: #f5f5f5;
            }

            i {
                font-size: 16px;
            }
        }
    }

    .EntityDetailsModal-Actions {
        display: flex;
        gap: 12px;
        justify-content: flex-end;
        padding-top: 20px;
        border-top: 0.5px solid var(--color-border-light);
    }

    @media (max-width: 768px) {
        .EntityDetailsModal {
            padding: 16px;
        }
    }
}
</style>
